import React, { ChangeEvent, FC, useState, useEffect } from 'react'
import { Dropdown, Accordion, Form } from 'react-bootstrap'
import { FILTER_SHOW_ALL } from '@constants/Notifications'
import './SearchWithFilter.css'

export type FilterSelection = Record<string, string[]>

type SearchWithFilterProps = {
  searchIcon?: string
  filterIcon?: string
  filterLabel?: string
  placeholder?: string
  searchQuery?: string
  disabled?: boolean
  ref?: React.RefObject<HTMLInputElement>
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onReset?: () => void
  showResetButton?: boolean
  className?: string
  filters: Record<string, string[]>
  onFilterChange: (selectedFilters: FilterSelection) => void
  /** When provided, these are the selected values (controlled). Enables preselecting e.g. tag from Charting History. */
  selectedFilters?: FilterSelection
  filterLabels?: Record<string, string>
  customRenderers?: Record<
    string,
    (
      option: string,
      isChecked: boolean,
      onChange: () => void,
    ) => React.ReactNode
  >
  /** When false, only the search input is shown (no filter dropdown). */
  showFilter?: boolean
}

const SearchWithFilter: FC<SearchWithFilterProps> = ({
  searchIcon,
  filterIcon = '',
  filterLabel = '',
  placeholder = '',
  searchQuery,
  disabled,
  ref,
  onChange,
  onReset,
  showResetButton = false,
  className = '',
  filters,
  onFilterChange,
  selectedFilters: selectedFiltersProp,
  filterLabels = {},
  customRenderers = {},
  showFilter = true,
}) => {
  const getInitialFilters = (): FilterSelection => {
    const initial: FilterSelection = {}
    if (filters.Category && filters.Category.length > 0) {
      const showAllIndex = filters.Category.findIndex(
        (item) => item === FILTER_SHOW_ALL,
      )
      if (showAllIndex !== -1) {
        initial.Category = [FILTER_SHOW_ALL]
      }
    }
    return initial
  }

  const [internalSelectedFilters, setInternalSelectedFilters] =
    useState<FilterSelection>(getInitialFilters())
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)

  const selectedFilters =
    selectedFiltersProp !== undefined && selectedFiltersProp !== null
      ? selectedFiltersProp
      : internalSelectedFilters

  useEffect(() => {
    if (selectedFiltersProp !== undefined && selectedFiltersProp !== null)
      return
    const initial = getInitialFilters()
    if (Object.keys(initial).length > 0) {
      setInternalSelectedFilters(initial)
      onFilterChange(initial)
    }
  }, [])

  const handleCheckboxChange = (category: string, value: string): void => {
    const updateFilters = (prevFilters: FilterSelection): FilterSelection => {
      const currentSelections = prevFilters[category] || []
      const hasShowAll = filters[category]?.includes(FILTER_SHOW_ALL) ?? false

      if (hasShowAll && value === FILTER_SHOW_ALL) {
        if (currentSelections.includes(FILTER_SHOW_ALL)) {
          const newSelections = currentSelections.filter(
            (item) => item !== FILTER_SHOW_ALL,
          )
          const updatedFilters = { ...prevFilters, [category]: newSelections }
          onFilterChange(updatedFilters)
          return updatedFilters
        } else {
          const updatedFilters = {
            ...prevFilters,
            [category]: [FILTER_SHOW_ALL],
          }
          onFilterChange(updatedFilters)
          return updatedFilters
        }
      } else {
        if (currentSelections.includes(value)) {
          const newSelections = currentSelections.filter(
            (item) => item !== value,
          )
          const finalSelections =
            hasShowAll && newSelections.length === 0
              ? [FILTER_SHOW_ALL]
              : newSelections.filter((item) => item !== FILTER_SHOW_ALL)
          const updatedFilters = { ...prevFilters, [category]: finalSelections }
          onFilterChange(updatedFilters)
          return updatedFilters
        } else {
          const newSelections = [
            ...currentSelections.filter((item) => item !== FILTER_SHOW_ALL),
            value,
          ]
          const updatedFilters = { ...prevFilters, [category]: newSelections }
          onFilterChange(updatedFilters)
          return updatedFilters
        }
      }
    }
    const next = updateFilters(selectedFilters)
    if (selectedFiltersProp === undefined || selectedFiltersProp === null) {
      setInternalSelectedFilters(next)
    }
  }

  const handleClearSearch = (): void => {
    const syntheticEvent = {
      target: { value: '' },
    } as ChangeEvent<HTMLInputElement>
    onChange?.(syntheticEvent)
  }

  const hasSearchValue = Boolean(searchQuery?.trim())

  return (
    <div className={`swf-container ${className}`}>
      <div className="swf-search">
        {searchIcon && <span className={searchIcon} />}
        <div className="swf-search-input-wrap">
          <input
            ref={ref}
            type={'text'}
            placeholder={placeholder}
            onChange={onChange}
            value={searchQuery}
            className="search-input"
            disabled={disabled}
            aria-disabled={disabled}
          />
          {hasSearchValue && (
            <button
              type="button"
              className="swf-search-clear"
              onClick={handleClearSearch}
              disabled={disabled}
              aria-label="Clear search"
            >
              <i className="mgc mgc_close_line" />
            </button>
          )}
        </div>
      </div>
      {showFilter && (
        <Dropdown
          show={dropdownOpen}
          drop="down"
          align="start"
          onToggle={(isOpen) => {
            if (disabled) {
              setDropdownOpen(false)
              return
            }
            if (isOpen === false) {
              const activeElement = document.activeElement as HTMLElement
              const hasDatePickerPopup =
                activeElement?.closest('.e-daterangepicker') ||
                activeElement?.closest('.e-popup') ||
                activeElement?.closest('.e-calendar') ||
                document.querySelector('.e-popup-open')

              if (hasDatePickerPopup) {
                return
              }
            }
            setDropdownOpen(isOpen)
          }}
          autoClose="outside"
        >
          <Dropdown.Toggle
            className={`filter-btn ${filterIcon}`}
            disabled={disabled}
            onClick={() => !disabled && setDropdownOpen(!dropdownOpen)}
          >
            {filterLabel}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="filter-dropdown"
            onClick={(e) => {
              e.stopPropagation()
            }}
            onMouseDown={(e) => {
              const target = e.target as HTMLElement
              if (
                target.closest('.custom-date-range-picker-in-dropdown') ||
                target.closest('.e-daterangepicker') ||
                target.closest('.e-popup') ||
                target.closest('.e-calendar')
              ) {
                e.stopPropagation()
                e.preventDefault()
              }
            }}
          >
            {showResetButton && onReset && (
              <div className="filter-reset-container">
                <button
                  type="button"
                  className="filter-reset-btn"
                  onClick={onReset}
                >
                  <i className="mgc mgc_refresh_2_line" />
                  Reset Filter
                </button>
              </div>
            )}
            <Accordion>
              {Object.keys(filters).map((key) => (
                <Accordion.Item eventKey={key} key={key}>
                  <Accordion.Header>
                    {filterLabels[key] || key}
                  </Accordion.Header>
                  <Accordion.Body>
                    {(filters[key] || []).map((option) => {
                      const customRenderer = customRenderers[`${key}:${option}`]
                      const isChecked = (selectedFilters[key] || []).includes(
                        option,
                      )

                      if (customRenderer) {
                        return (
                          <div key={option}>
                            {customRenderer(option, isChecked, () =>
                              handleCheckboxChange(key, option),
                            )}
                          </div>
                        )
                      }

                      return (
                        <Form.Check
                          key={option}
                          type="checkbox"
                          label={option}
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(key, option)}
                        />
                      )
                    })}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Dropdown.Menu>
        </Dropdown>
      )}
    </div>
  )
}

export default SearchWithFilter
